(function () {
  /*
   * je-view-transition-guard-20261015: the theme's own transition settings decide, not the browser's
   * opt-in alone. layout/theme.liquid prints both settings on main#MainContent
   * (data-page-transition-enabled, data-product-transition) and, when BOTH are off, adds
   * `@view-transition { navigation: none }` so no cross-document transition starts at all. This file
   * agrees with it: a plain page navigation only animates when page transitions are on, a product
   * image transition only when the product transition is on. Missing APIs, blocked storage and an
   * aborted or stalled transition all end in a normally painted page, never an exception or a
   * transition overlay left in front of it.
   */
  const CUSTOM_TYPE_KEY = 'custom-transition-type';
  /* The theme's transitions run in well under a second. A transition still unfinished after this
     long is stalled (throttled animations, GPU pressure, a hidden tab) and is skipped so its
     overlay cannot keep the new page out of sight. */
  const STALLED_TRANSITION_MS = 4000;

  const viewTransitionRenderBlocker = document.getElementById('view-transition-render-blocker');
  // Remove the view transition render blocker if the user has reduced motion enabled or is on a low power device.
  if (prefersReducedMotion() || isLowPowerDevice()) {
    viewTransitionRenderBlocker?.remove();
  } else {
    // If the browser didn't manage to parse the main content quickly, at least let the user see something.
    // We're aiming for the FCP to be under 1.8 seconds since the navigation started.
    const RENDER_BLOCKER_TIMEOUT_MS = Math.max(0, 1800 - performance.now());

    setTimeout(() => {
      viewTransitionRenderBlocker?.remove();
    }, RENDER_BLOCKER_TIMEOUT_MS);
  }

  const idleCallback = typeof requestIdleCallback === 'function' ? requestIdleCallback : setTimeout;

  /** @type {Array<[Element, string]>} attributes pageswap took off, put back if this page returns from the bfcache */
  let strippedTransitionTypes = [];
  /** @type {ViewTransition | null} */
  let activeTransition = null;
  /** Removes the outgoing page's interaction-skip listeners; replaced on each pageswap. */
  let removeOutgoingSkip = () => {};

  window.addEventListener('pageswap', (event) => {
    const { viewTransition } = /** @type {PageSwapEvent} */ (event);
    // je-scroll-fx-20261029: a tapped product photo flying into its page (or back) opted this navigation in;
    // assets/je-scroll-fx-20261029.js and its head script decide whether it runs
    if (document.getElementById('je-sfx-vt-on')) return;

    if (shouldSkipViewTransition(viewTransition)) {
      skip(viewTransition);
      storageRemove(CUSTOM_TYPE_KEY);
      return;
    }

    // Cancel view transition on user interaction to improve INP (Interaction to Next Paint).
    // The listeners go once the transition ends, so a page restored from the bfcache does not keep them.
    removeOutgoingSkip();
    const skipOnInput = () => skip(viewTransition);
    ['pointerdown', 'keydown'].forEach((eventName) => {
      document.addEventListener(eventName, skipOnInput, { once: true });
    });
    removeOutgoingSkip = () => {
      ['pointerdown', 'keydown'].forEach((eventName) => document.removeEventListener(eventName, skipOnInput));
      removeOutgoingSkip = () => {};
    };
    try {
      const done = () => removeOutgoingSkip();
      viewTransition.finished?.then(done, done);
    } catch (error) {
      removeOutgoingSkip();
    }

    // Clean in case you landed on the pdp first. We want to remove the default transition type on the PDP media gallery so there is no duplicate transition name
    strippedTransitionTypes = [];
    document
      .querySelectorAll('[data-view-transition-type]:not([data-view-transition-triggered])')
      .forEach((element) => {
        strippedTransitionTypes.push([element, element.getAttribute('data-view-transition-type') || '']);
        element.removeAttribute('data-view-transition-type');
      });

    const transitionTriggered = document.querySelector('[data-view-transition-triggered]');
    const transitionType = transitionTriggered?.getAttribute('data-view-transition-type');
    const flags = transitionFlags();

    if (transitionType && flags.product !== false) {
      setTypes(viewTransition, transitionType);
      storageSet(CUSTOM_TYPE_KEY, transitionType);
    } else if (flags.page === false) {
      // Page transitions are switched off and this is not a product transition.
      skip(viewTransition);
      storageRemove(CUSTOM_TYPE_KEY);
    } else {
      setTypes(viewTransition, 'page-navigation');
      storageRemove(CUSTOM_TYPE_KEY);
    }
  });

  window.addEventListener('pagereveal', (event) => {
    const { viewTransition } = /** @type {PageRevealEvent} */ (event);
    // je-scroll-fx-20261029: a tapped product photo flying into its page (or back) opted this navigation in;
    // assets/je-scroll-fx-20261029.js and its head script decide whether it runs
    if (document.getElementById('je-sfx-vt-on')) return;
    // Read once and remove at once: a type left in storage would be applied to some later, unrelated navigation.
    const customTransitionType = storageGet(CUSTOM_TYPE_KEY);
    storageRemove(CUSTOM_TYPE_KEY);

    if (shouldSkipViewTransition(viewTransition)) {
      skip(viewTransition);
      return;
    }

    const flags = transitionFlags();

    if (customTransitionType && flags.product !== false) {
      setTypes(viewTransition, customTransitionType);
      watch(viewTransition, () => {
        document.querySelectorAll('[data-view-transition-type]').forEach((element) => {
          element.removeAttribute('data-view-transition-type');
        });
      });
    } else if (flags.page === false) {
      skip(viewTransition);
    } else {
      setTypes(viewTransition, 'page-navigation');
      watch(viewTransition, null);
    }
  });

  // Leaving for the bfcache mid-transition: end it, so the page is not frozen behind its overlay.
  window.addEventListener('pagehide', () => {
    skip(activeTransition);
    activeTransition = null;
  });

  // Back from the bfcache: the transition that took this page away is over. Its trigger mark is stale
  // and the gallery types pageswap removed belong back on their elements.
  window.addEventListener('pageshow', (event) => {
    if (!(/** @type {PageTransitionEvent} */ (event).persisted)) return;
    removeOutgoingSkip();
    try {
      document.querySelectorAll('[data-view-transition-triggered]').forEach((element) => {
        element.removeAttribute('data-view-transition-triggered');
      });
      strippedTransitionTypes.forEach(([element, type]) => {
        if (element.isConnected && type && !element.hasAttribute('data-view-transition-type')) {
          element.setAttribute('data-view-transition-type', type);
        }
      });
    } catch (error) {
      // Restoring decoration must never stop the page from showing.
    }
    strippedTransitionTypes = [];
  });

  /**
   * Follow a revealed transition to its end, whatever that end is. A user's tap or key press, or a
   * transition still running after STALLED_TRANSITION_MS, skips it; `onEnd` always runs.
   * @param {ViewTransition} viewTransition
   * @param {(() => void) | null} onEnd
   */
  function watch(viewTransition, onEnd) {
    activeTransition = viewTransition;
    const stop = () => skip(viewTransition);
    const stalled = setTimeout(stop, STALLED_TRANSITION_MS);
    // ::view-transition is pointer-events: none, so these reach the new page underneath it.
    document.addEventListener('pointerdown', stop, { once: true, capture: true });
    document.addEventListener('keydown', stop, { once: true, capture: true });

    let ended = false;
    const end = () => {
      if (ended) return;
      ended = true;
      clearTimeout(stalled);
      document.removeEventListener('pointerdown', stop, { capture: true });
      document.removeEventListener('keydown', stop, { capture: true });
      if (activeTransition === viewTransition) activeTransition = null;
      if (onEnd) {
        idleCallback(() => {
          try {
            onEnd();
          } catch (error) {
            // Cleanup is best effort.
          }
        });
      }
    };

    try {
      const finished = viewTransition.finished;
      if (finished && typeof finished.then === 'function') {
        // An aborted transition rejects or resolves early; either way the cleanup runs.
        finished.then(end, end);
      } else {
        end();
      }
    } catch (error) {
      end();
    }
  }

  /**
   * @param {ViewTransition | null | undefined} viewTransition
   */
  function skip(viewTransition) {
    try {
      if (viewTransition && typeof viewTransition.skipTransition === 'function') viewTransition.skipTransition();
    } catch (error) {
      // Already finished or never started: nothing to skip.
    }
  }

  /**
   * @param {ViewTransition} viewTransition
   * @param {string} type
   */
  function setTypes(viewTransition, type) {
    try {
      const types = viewTransition.types;
      if (!types || typeof types.clear !== 'function' || typeof types.add !== 'function') return;
      types.clear();
      types.add(type);
    } catch (error) {
      // Transition types unsupported: the browser's default cross-fade applies.
    }
  }

  /**
   * The settings as main#MainContent printed them. `null` means "not on this page yet", which keeps
   * the browser's own decision rather than guessing.
   * @returns {{page: boolean | null, product: boolean | null}}
   */
  function transitionFlags() {
    const main = document.getElementById('MainContent');
    return {
      page: readFlag(main, 'data-page-transition-enabled'),
      product: readFlag(main, 'data-product-transition'),
    };
  }

  /**
   * @param {Element | null} element
   * @param {string} name
   * @returns {boolean | null}
   */
  function readFlag(element, name) {
    const value = element?.getAttribute(name);
    if (value === 'true') return true;
    if (value === 'false') return false;
    return null;
  }

  /**
   * @param {ViewTransition | null} viewTransition
   * @returns {viewTransition is null}
   */
  function shouldSkipViewTransition(viewTransition) {
    if (!viewTransition || typeof ViewTransition !== 'function' || !(viewTransition instanceof ViewTransition)) {
      return true;
    }
    if (prefersReducedMotion() || isLowPowerDevice()) return true;
    const flags = transitionFlags();
    return flags.page === false && flags.product === false;
  }

  function prefersReducedMotion() {
    try {
      return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (error) {
      return false;
    }
  }

  /** @param {string} key */
  function storageGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  /**
   * @param {string} key
   * @param {string} value
   */
  function storageSet(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      // Storage blocked or full: the next page falls back to a plain page transition.
    }
  }

  /** @param {string} key */
  function storageRemove(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      // Storage blocked: nothing was stored.
    }
  }

  /*
   * We can't import this logic from utilities.js here, but we should keep them in sync.
   */
  function isLowPowerDevice() {
    /* Skip ESLint compatibility check. Number(undefined) <= 2 is always false anyway. */
    /* eslint-disable-next-line compat/compat */
    return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
  }
})();
